/**
 * StammdatenTab — Sprint-16 nach Mockup 4e.
 *
 * Content-Header: Eyebrow „— Stammdaten" + Serif „Wem gehört welches Feld."
 * + Sub-Copy. Actions rechts sind Teil vom PropertyForm (Verwerfen + Speichern
 * am Boden). Auto-Save-Timestamp „Gespeichert vor X Min" oben.
 *
 * StayInfoCard und InvoiceTemplateAssignCard bleiben unter dem PropertyForm —
 * Mockup zeigt sie zwar nicht explizit, sind aber operative StayOn-Only-Data.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Check } from 'lucide-react';
import { Eyebrow } from '@/shared/components';
import { toApiError, useApi } from '@/shared/api';
import type { UpdatePropertyRequest } from '@/shared/api';
import { formatRelative } from '@/shared/lib/format';
import { PropertyForm } from '../property-form';
import type { PropertyValues } from '../property-form/schema';
import { ApartmentObjectAssignment } from '../property-form/ApartmentObjectAssignment';
import { FlatFamilyCard } from '../flat-family';
import { propertyKeys } from '../queries';
import { useApartmentDetail } from './ApartmentDetailLayout';
import { InvoiceTemplateAssignCard } from './InvoiceTemplateAssignCard';
import { InvoiceBlocksSection } from './invoice-blocks/InvoiceBlocksSection';
import { StayInfoCard } from './StayInfoCard';
import { ApartmentArrivalCard } from './ApartmentArrivalCard';

function toUpdatePayload(
  values: PropertyValues,
  property: { address: UpdatePropertyRequest['address']; isActive: boolean },
): UpdatePropertyRequest {
  return {
    name: values.name,
    description: values.description,
    propertyType: values.propertyType,
    timezone: values.timezone,
    address: property.address,
    occupancyAdults: values.occupancyAdults,
    occupancyChildren: values.occupancyChildren,
    bedrooms: values.bedrooms,
    bathrooms: values.bathrooms,
    currency: values.currency.toUpperCase(),
    cleaningFee: values.cleaningFee,
    baseNightlyRate: values.baseNightlyRate,
    isActive: property.isActive,
    operationalName: values.operationalName,
  };
}

export function StammdatenTab() {
  const { propertyId: id } = useApartmentDetail();
  const api = useApi();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: property, isLoading } = useQuery({
    queryKey: propertyKeys.detail(id),
    queryFn: () => api.properties.get(id),
  });

  const update = useMutation({
    mutationFn: async (values: UpdatePropertyRequest) => {
      await api.properties.update(id, values);
      toast.success('Stammdaten aktualisiert');
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: propertyKeys.lists() });
      await qc.invalidateQueries({ queryKey: propertyKeys.detail(id) });
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  if (isLoading || !property) {
    return <p className="text-ink-soft">Lade…</p>;
  }

  return (
    <>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <Eyebrow>Stammdaten</Eyebrow>
          <h2 className="text-ink mt-3 font-serif text-3xl font-normal tracking-tight">
            Wem gehört welches <span className="font-serif-italic">Feld</span>
            <span className="text-sealwax">.</span>
          </h2>
          <p className="text-ink-soft mt-2 max-w-prose text-sm">
            Jede Karte zeigt, wohin die Felder gehen und wofür sie verwendet werden.
          </p>
        </div>
        {property.updatedAt && (
          <div className="text-ink-faint flex items-center gap-1.5 text-xs">
            <Check size={12} strokeWidth={2} className="text-sage" />
            <span>Gespeichert {formatRelative(property.updatedAt)}</span>
          </div>
        )}
      </header>

      <ApartmentObjectAssignment property={property} />
      <FlatFamilyCard property={property} />
      <PropertyForm
        initial={property}
        submitting={update.isPending}
        onSubmit={async (values) => {
          await update.mutateAsync(toUpdatePayload(values, property));
        }}
        onCancel={() => void navigate({ to: '/properties/$id', params: { id } })}
        submitLabel="Speichern"
      />

      <div className="mt-6">
        <StayInfoCard />
      </div>
      <div className="mt-6">
        <ApartmentArrivalCard />
      </div>
      <div className="mt-6">
        <InvoiceTemplateAssignCard property={property} />
      </div>
      <div className="mt-6">
        <InvoiceBlocksSection property={property} />
      </div>
    </>
  );
}
